import { Button } from "components/atoms/Button";
import Dropdown from "components/atoms/Dropdown";
import CreateCustomPlanModal from "components/organisms/Modals/CreateCustomPlanModal/createCustomPlanModal";
import posthog from "posthog-js";
import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { PlanDropDownPresenter, PlanViews } from "./PlanDropDown/PlanDropDownPresenter";

const createPlanPresentersMap = (plans: BuildingModelCarbonReductionPlan[], planView: PlanViews) => {
    const reportedPlan = plans.filter((plan) => plan.isReported)[0];
    const customPlans = plans.filter((plan) => plan.planType === "custom");
    const presenters = customPlans.map(
        (plan) => new PlanDropDownPresenter(plan as BuildingModelCarbonReductionPlan, planView, false)
    );
    presenters.push(new PlanDropDownPresenter(reportedPlan, planView, true));
    return presenters.reduce<Record<string, PlanDropDownPresenter>>((acc, obj) => {
        acc[obj.getID()] = obj;
        return acc;
    }, {});
};

interface PlanDropDownProps {
    buildingModel: Building;
    selectedBMCRP: BuildingModelCarbonReductionPlan | null;
}

function PlanDropDown({ buildingModel, selectedBMCRP }: PlanDropDownProps) {
    const navigate = useNavigate();
    const { planView } = useParams();
    const [modalOpen, setModalOpen] = useState(false);
    const buildingModelCarbonReductionPlanId = selectedBMCRP?.id;
    const location = useLocation();
    const segments = location.pathname.split("/");
    const currentTab = segments[segments.length - 1];

    const planPresenters = createPlanPresentersMap(
        buildingModel.carbonReductionPlans as BuildingModelCarbonReductionPlan[],
        planView as PlanViews
    );

    const sortedPresenters = Object.values(planPresenters).sort(
        (a, b) => Number(b.recommended) - Number(a.recommended)
    );
    const items = sortedPresenters.map((presenter) => ({
        id: presenter.getID(),
        displayValue: presenter.getDisplayValue(),
        element: presenter.getElement(),
        disable: false,
    }));
    items.push({
        displayValue: "+ New plan",
        id: "new-plan-button",
        disable: true,
        element: (
            <div style={{ flex: 1 }}>
                <hr
                    style={{
                        border: "none",
                        height: "1px",
                        backgroundColor: "#ccc",
                    }}
                />
                <Button style={{ color: "blue" }} onClick={() => setModalOpen(true)}>
                    + New Plan
                </Button>
            </div>
        ),
    });

    const handleSelectItem = (id: string) => {
        if (!(id in planPresenters)) {
            console.error("Selected plan ID not in list of presenters.");
            return;
        }
        navigate(planPresenters[id].getURL(currentTab));
        posthog.capture("Building plan selection updated.");
        // [2026-01-07 TRC] I don't understand why we'd want to invalidate the Portfolio page when we just switch plans.  Commenting out to see what happens
        // resetBuildingsTable();
        // resetPropertiesTable();
    };

    if (items?.length === 0) {
        return <div />;
    }

    if (!buildingModel.reportedCrp) {
        return <div />;
    }

    const defaultItem = items.find(({ id }) =>
        planPresenters[id] ? planPresenters[id].match(planView, buildingModelCarbonReductionPlanId!) : false
    );
    return (
        <>
            <Dropdown
                hideTitle={false}
                items={items}
                defaultItem={defaultItem}
                required={false}
                onSelectItem={({ id }) => handleSelectItem(id)}
                selectId="building-page-selected-plan"
                style={{ margin: 0 }}
            />
            <CreateCustomPlanModal
                buildingModelUid={buildingModel.buildingModelUid}
                setModalIsOpen={setModalOpen}
                onCancel={() => setModalOpen(false)}
                modalIsOpen={modalOpen}
                carbonReductionPlanUid={buildingModel.reportedCrp?.carbonReductionPlanUid}
            />
        </>
    );
}

export default PlanDropDown;
